import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import "./ToggleSwitchButtonMission.scss";
import "core/inputs/input-text/InputText.scss";
import Icon from "core/icon/Icon";
import colors from "assets/styles/mixins/colors.scss";
import { FormattedMessage } from "react-intl";
import { InputTextPopup } from "core/inputs/input-text/InputText";
import PopupLogo from "assets/icons/popup.svg";
import { renderIf } from "utils/helper";
import ErrorPopupBtn from "components/alert-popup/ErrorPopupBtn";
import { ErrorPopup } from "components/alert-popup/ErrorPopup";

const ToggleSwitchButtonMission = ({
  data,
  onChange,
  errors,
  value,
  id,
  readOnly,
}) => {
  const [isChecked, setIsChecked] = useState(null);
  const inputRef = useRef(null);
  const [color, setColor] = useState(colors.switchGrey);
  const [iconColor, setIconColor] = useState(colors.switchBlue);
  const [sliderPosition, setSliderPosition] = useState("center");
  const [textRightColor, setTextRightColor] = useState(colors.switchGrey);
  const [textLeftColor, setTextLeftColor] = useState(colors.switchGrey);
  const [popupOpened, setPopupOpened] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);

  const {
    iconTrue,
    iconFalse,
    bgColor,
    iconColorTrue,
    iconColorFalse,
    height,
    width,
    widthFalse,
    heightFalse,
    marginLeft,
    marginTop,
    marginTopFalse,
    marginLeftFalse,
    href,
  } = data;

  const { title_right, title_left, title, content } = data.topUp;

  const getBgColor = useCallback(
    (sliderPosition) => {
      switch (sliderPosition) {
        case "left":
          setColor(colors.switchBlue);
          setIconColor(iconColorFalse);
          setTextLeftColor(colors.violet);
          setTextRightColor(colors.switchGrey);
          break;
        case "right":
          setColor(bgColor);
          setIconColor(iconColorTrue);
          setTextRightColor(colors.violet);
          setTextLeftColor(colors.switchGrey);
          break;
        default:
          setColor(colors.switchGrey);
          setTextLeftColor(colors.switchGrey);
          setTextRightColor(colors.switchGrey);
          break;
      }
    },
    [bgColor, iconColorFalse, iconColorTrue, setColor, setIconColor]
  );

  const handleChange = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  useEffect(() => {
    if (onChange && isChecked !== null) {
      onChange(isChecked);
    }
  }, [isChecked]);

  useEffect(() => {
    if (isChecked !== null) {
      setSliderPosition(renderIf(isChecked, "right", "left"));
    }
    getBgColor(sliderPosition);
  }, [getBgColor, isChecked, sliderPosition]);

  const handleSliderClick = (e) => {
    if (readOnly) return;

    e.preventDefault();
    e.stopPropagation();

    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const sliderWidth = rect.width;
    const middle = sliderWidth / 2;

    if (clickX < middle) {
      setIsChecked(false);
    } else {
      setIsChecked(true);
    }
  };

  const setToLeft = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!readOnly) {
      setIsChecked(false);
    }
  };

  const setToRight = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!readOnly) {
      setIsChecked(true);
    }
  };

  const handleMouseOn = () => {
    setPopupOpened(true);
  };
  const handleMouseLeave = () => {
    setPopupOpened(false);
  };

  useLayoutEffect(() => {
    setIsChecked(value);
  }, [value]);

  return (
    title_left &&
    title_right &&
    title &&
    content && (
      <div className="switch">
        <span
          id={`falseText-${id}`}
          className="falseText"
          onClick={setToLeft}
          style={{
            fontWeight: "bold",
            transition: "all 0.2s ease-in-out",
            width: "75px",
            color: errors?.type || errors?.message ? "red" : textLeftColor,
            cursor: readOnly ? "default" : "pointer",
          }}
        >
          <FormattedMessage
            id={title_left.id}
            defaultMessage={title_left.defaultMessage}
          />
        </span>
        <label
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setToLeft(e);
          }}
        ></label>
        <span>
          <input
            type="checkbox"
            id={`toggleInput-${id}`}
            checked={isChecked}
            onChange={handleChange}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            ref={inputRef}
          />
          <button
            style={{ background: color }}
            className={`slider ${sliderPosition}`}
            type="button"
            onClick={handleSliderClick}
          >
            {isChecked !== null && (
              <Icon
                icon={renderIf(sliderPosition === "right", iconTrue, iconFalse)}
                color={iconColor}
                height={renderIf(
                  sliderPosition === "right",
                  height,
                  heightFalse
                )}
                width={renderIf(sliderPosition === "right", width, widthFalse)}
                marginLeft={renderIf(
                  sliderPosition === "right",
                  marginLeft,
                  marginLeftFalse
                )}
                marginTop={renderIf(
                  sliderPosition === "left",
                  marginTopFalse,
                  marginTop
                )}
                position={`icon-position ${sliderPosition}`}
              />
            )}
          </button>
        </span>
        <span
          id={`trueText-${id}`}
          className="trueText"
          onClick={(e) => {
            if (href) {
              return;
            }
            setToRight(e);
          }}
          style={{
            fontWeight: "bold",
            transition: "all 0.2s ease-in-out",
            width: "60px",
            color: errors?.type || errors?.message ? "red" : textRightColor,
            textDecoration: renderIf(href, "underline", ""),
            cursor: readOnly ? "default" : "pointer",
          }}
        >
          {href ? (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: errors?.type || errors?.message ? "red" : textRightColor,
              }}
            >
              <FormattedMessage
                id={title_right.id}
                defaultMessage={title_right.defaultMessage}
              />
            </a>
          ) : (
            <FormattedMessage
              id={title_right.id}
              defaultMessage={title_right.defaultMessage}
            />
          )}
        </span>
        {title && (
          <div className="label sourcesanspro-normal-shadow-blue-14px error">
            {(errors?.type || errors?.message) && (
              <ErrorPopupBtn setShowErrorPopup={setShowErrorPopup} />
            )}
            {showErrorPopup && (
              <ErrorPopup
                errors={errors}
                showErrorPopup={showErrorPopup}
                setShowErrorPopup={setShowErrorPopup}
                label={title}
                errorMessage={errors?.message}
              />
            )}
            {data.topUp && (
              <span
                className="popup-information"
                onClick={() => setPopupOpened(!popupOpened)}
              >
                <img
                  src={PopupLogo}
                  alt="logo"
                  onMouseEnter={handleMouseOn}
                  onMouseLeave={handleMouseLeave}
                />
              </span>
            )}
          </div>
        )}
        {popupOpened && <InputTextPopup topUp={data.topUp} />}
      </div>
    )
  );
};

ToggleSwitchButtonMission.propTypes = {
  data: PropTypes.object,
  onChange: PropTypes.func,
  errors: PropTypes.shape({
    message: PropTypes.string,
  }),
  value: PropTypes.any,
  id: PropTypes.string,
  readOnly: PropTypes.bool,
};

export default ToggleSwitchButtonMission;
